// polyfills must register before any other module (vendor code included)
// evaluates, so these two imports stay first
import 'core-js/stable';
import 'regenerator-runtime/runtime';
import '@formatjs/intl-getcanonicallocales/polyfill';
import '@formatjs/intl-locale/polyfill';
import '@formatjs/intl-numberformat/polyfill';
import '@formatjs/intl-pluralrules/locale-data/de.js';
import '@formatjs/intl-pluralrules/locale-data/en.js';
import '@formatjs/intl-pluralrules/locale-data/es.js';
import '@formatjs/intl-pluralrules/locale-data/fr.js';
import '@formatjs/intl-pluralrules/locale-data/nl.js';
import '@formatjs/intl-pluralrules/polyfill';
import '@formatjs/intl-relativetimeformat/locale-data/de.js';
import '@formatjs/intl-relativetimeformat/locale-data/en.js';
import '@formatjs/intl-relativetimeformat/locale-data/es.js';
import '@formatjs/intl-relativetimeformat/locale-data/fr.js';
import '@formatjs/intl-relativetimeformat/locale-data/nl.js';
import '@formatjs/intl-relativetimeformat/polyfill';
import { ThemeProvider as MUIThemeProvider } from '@mui/material';
import axios from 'axios';
import 'intersection-observer';
import 'intl-tel-input/build/css/intlTelInput.css';
import 'moment';
import moment from 'moment-timezone';
import React, { useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import {
	createBrowserRouter,
	createRoutesFromElements,
	Navigate,
	Outlet,
	Route,
	RouterProvider,
	useParams,
} from 'react-router-dom';
import 'reactjs-popup/dist/index.css';
import 'url-search-params-polyfill';
import { onWindowResize, setDeviceType, setOnlineStatus } from './actions/siteActions';
import RouteLinks from './constants/routes.js';
import './css/styles.css';
import { getStore } from './store';
import PostHogPageView from './utils/pageview.js';
import { PostHogProvider } from './utils/posthog-provider';

import { SpinnerWithWrapper } from './components/common/spinner';
import ErrorBoundary from './components/error-boundary';
import Errorpage from './components/errorpage';
import Layout from './components/Layout';
import Authorisation from './containers/authorisation';
import LoginRegisterContainer from './containers/login-register-container';
import Unauthorised from './pages/Error/unauthorised';
import { GlobalStyles } from './themes/global';
import customMuiTheme from './themes/mui-theme';
import { lazyWithRetry } from './utils/lazyWithRetry';
import IntlWrapper from './utils/localisation/IntlWrapper';
import { resolveLocale } from './utils/localisation/locales.js';
import { getLocalStorageItem } from './utils/safe-storage';

import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';

const Page = (props) => {
	useEffect(() => {
		document.title = props.title || 'RefNow';
	}, [props.title]);

	return (
		<>
			<PostHogPageView />
			{props.children}
		</>
	);
};

export default Page;

const intlOpts = Intl.DateTimeFormat().resolvedOptions();

moment.locale(intlOpts.locale);

//IMPORTANT: Set the default timezone to London as this is where our servers are located
// The default timezone needs to be set and then timezones can be applied when using moment.tz()
moment.tz.setDefault('Europe/London');

//Login/Register
const Login = lazyWithRetry(() => import(/* webpackChunkName: "login" */ './pages/Login'));
const Registration = lazyWithRetry(() =>
	import(/* webpackChunkName: "register" */ './pages/Register')
);

//References
const Requests = lazyWithRetry(() => import(/* webpackChunkName: "requests" */ './pages/Requests'));

//Admin Pages
const TalentPool = lazyWithRetry(() =>
	import(/* webpackChunkName: "talentPool" */ './pages/TalentPool')
);
const VacancyPool = lazyWithRetry(() =>
	import(/* webpackChunkName: "vacancyPool" */ './pages/VacancyPool')
);
const QuestionProfileHomepage = lazyWithRetry(() =>
	import(/* webpackChunkName: "questionProfiles" */ './pages/QuestionProfiles')
);
//Settings
const Settings = lazyWithRetry(() => import(/* webpackChunkName: "settings" */ './pages/Settings'));
const AccountSettings = lazyWithRetry(() =>
	import(/* webpackChunkName: "accountSettings" */ './pages/Settings/account')
);
const OrganisationSettings = lazyWithRetry(() =>
	import(/* webpackChunkName: "orgSettings" */ './pages/Settings/organisation')
);
const NotificationSettings = lazyWithRetry(() =>
	import(/* webpackChunkName: "notificationsSettings" */ './pages/Settings/notifications')
);
const SubscriptionSettings = lazyWithRetry(() =>
	import(/* webpackChunkName: "subscriptionSettings" */ './pages/Settings/Subscription')
);
const BuyCredits = lazyWithRetry(() =>
	import(/* webpackChunkName: "buyCredits" */ './pages/Settings/buy-credits')
);
const UsersAndTeams = lazyWithRetry(() =>
	import(/* webpackChunkName: "usersAndTeams" */ './pages/Settings/UsersAndTeams')
);
const HelpCentre = lazyWithRetry(() =>
	import(/* webpackChunkName: "HelpCentre" */ './pages/Settings/HelpCentre')
);
const ReferencingDashboard = lazyWithRetry(() =>
	import(/* webpackChunkName: "referencingDashboard" */ './pages/Requests/dashboard')
);
const Reporting = lazyWithRetry(() => import(/* webpackChunkName: "reporting" */ './pages/Reporting'));
const Surveys = lazyWithRetry(() => import(/* webpackChunkName: "surveys" */ './pages/Surveys'));
const SurveyForm = lazyWithRetry(() => import(/* webpackChunkName: "surveyForm" */ './pages/Forms/Survey'));
const InboundDashboard = lazyWithRetry(() =>
	import(/* webpackChunkName: "inbound" */ './pages/Inbound/Dashboard')
);
const FeaturePreview = lazyWithRetry(() =>
	import(/* webpackChunkName: "featurePreview" */ './pages/FeaturePreview')
);
//Email Forms
const EmailFormContainer = lazyWithRetry(() =>
	import(/* webpackChunkName: "emailFormContainer" */ './containers/email-form-container')
);
const AddRefereeForm = lazyWithRetry(() =>
	import(/* webpackChunkName: "addRefereeForm" */ './components/EmailForms/add-referee-form')
);
const AddReferenceForm = lazyWithRetry(() =>
	import(/* webpackChunkName: "addReferenceForm" */ './components/EmailForms/add-reference-form')
);
const RequestInviteForm = lazyWithRetry(() =>
	import(
		/* webpackChunkName: "requestInviteForm" */ './components/EmailForms/request-invite-form'
	)
);
const ForgotPasswordForm = lazyWithRetry(() =>
	import(
		/* webpackChunkName: "forgotPasswordForm" */ './components/EmailForms/forgot-password-form'
	)
);
const SetPasswordForm = lazyWithRetry(() =>
	import(/* webpackChunkName: "setPasswordForm" */ './components/EmailForms/set-password-form')
);

const lang = resolveLocale(getLocalStorageItem('locale'));

axios.defaults.headers.common['Accept-Language'] = lang;

const { store } = getStore();

const ProtectedRoutes = ({ isAdmin, isSuperAdmin, navigateTo = RouteLinks.unauthorised }) => {
	const { user } = store.getState();
	const adminRoute = isAdmin && user.isAdmin;
	const superAdminRoute = isSuperAdmin && user.isSuperAdmin;
	const authenticated = adminRoute || superAdminRoute;

	return authenticated ? <Outlet /> : <Navigate to={navigateTo} />;
};

const NoMatch = () => (
	<Page title="RefNow | 404">
		<Errorpage />
	</Page>
);

const getEmployerRoute = () => {
	const searchParams = new URLSearchParams(window.location.search);

	return `${RouteLinks.employer}?${searchParams.toString()}`;
};

const SuspenseLoader = ({ children }) => (
	<React.Suspense fallback={<SpinnerWithWrapper size="MD" />}>{children}</React.Suspense>
);

// Old exit-survey links (emails already sent, bookmarks) land on the surveys
// routes that replaced them. The backend no longer serves exit-surveys.
const RedirectWithParams = ({ to }) => {
	const params = useParams();
	const target = to.replace(/:(\w+)/g, (match, name) => encodeURIComponent(params[name] ?? ''));

	return <Navigate to={`${target}${window.location.search}`} replace />;
};

const router = createBrowserRouter(
	createRoutesFromElements(
		<Route errorElement={<NoMatch />}>
			<Route path={RouteLinks.surveyForm} element={<SuspenseLoader><SurveyForm /></SuspenseLoader>} />
			<Route
				path={RouteLinks.exitSurveyForm}
				element={<RedirectWithParams to={RouteLinks.surveyForm} />}
			/>
			<Route
				path="/"
				element={
					<SuspenseLoader>
						<LoginRegisterContainer />
					</SuspenseLoader>
				}
			>
				<Route path="/" element={<Navigate to={getEmployerRoute()} />} />
				<Route
					path={RouteLinks.login}
					element={
						<Page title="RefNow | Login">
							<Login />
						</Page>
					}
				/>
				<Route
					path={RouteLinks.register}
					element={
						<Page title="RefNow | Register">
							<Registration />
						</Page>
					}
				/>
			</Route>
			<Route
				path="/"
				element={
					<SuspenseLoader>
						<EmailFormContainer />
					</SuspenseLoader>
				}
			>
				<Route path={RouteLinks.addReferees} element={<AddRefereeForm />} />
				<Route path={RouteLinks.addReference} element={<AddReferenceForm />} />
				<Route path={RouteLinks.forgotPassword} element={<ForgotPasswordForm />} />
				<Route path={RouteLinks.setPassword} element={<SetPasswordForm />} />
				<Route path={RouteLinks.requestInvite} element={<RequestInviteForm />} />
			</Route>
			<Route path="/" element={<Authorisation />}>
				<Route path={RouteLinks.employer} element={<Layout />}>
					<Route path={RouteLinks.surveys} element={<SuspenseLoader><Page title="RefNow | Surveys"><Surveys /></Page></SuspenseLoader>} />
					<Route path={`${RouteLinks.surveys}/templates/:templateId`} element={<SuspenseLoader><Page title="RefNow | Survey template"><Surveys /></Page></SuspenseLoader>} />
					<Route path={`${RouteLinks.surveys}/imports/:importId`} element={<SuspenseLoader><Page title="RefNow | Survey import"><Surveys /></Page></SuspenseLoader>} />
					<Route path={`${RouteLinks.surveys}/:surveyId`} element={<SuspenseLoader><Page title="RefNow | Survey"><Surveys /></Page></SuspenseLoader>} />
					<Route
						path={RouteLinks.exitSurveys}
						element={<Navigate to={RouteLinks.surveys} replace />}
					/>
					<Route
						path={`${RouteLinks.exitSurveys}/:surveyId`}
						element={<RedirectWithParams to={`${RouteLinks.surveys}/:surveyId`} />}
					/>
					<Route
						index
						element={
							<SuspenseLoader>
								<Page title="RefNow | Requests">
									<Requests />
								</Page>
							</SuspenseLoader>
						}
					/>
					<Route
						exact
						path={RouteLinks.employerDashboard}
						element={
							<SuspenseLoader>
								<Page title="RefNow | Dashboard">
									<ReferencingDashboard />
								</Page>
							</SuspenseLoader>
						}
					/>
					<Route
						path={RouteLinks.reporting}
						element={<Navigate to={RouteLinks.reportingCandidateSummary} replace />}
					/>
					<Route
						exact
						path={`${RouteLinks.reporting}/:reportKey`}
						element={
							<SuspenseLoader>
								<Page title="RefNow | Reporting">
									<Reporting />
								</Page>
							</SuspenseLoader>
						}
					/>
					<Route
						exact
						path={RouteLinks.adminQuestionProfiles}
						element={
							<SuspenseLoader>
								<Page title="RefNow | Question Profiles">
									<QuestionProfileHomepage />
								</Page>
							</SuspenseLoader>
						}
					/>
					<Route
						exact
						path={RouteLinks.preview}
						element={
							<SuspenseLoader>
								<Page title="RefNow | Feature Preview">
									<FeaturePreview />
								</Page>
							</SuspenseLoader>
						}
					/>
					<Route
						element={
							<ProtectedRoutes
								isAdmin={true}
								isSuperAdmin={true}
								navigateTo={`${RouteLinks.preview}?product=talentPool`}
							/>
						}
					>
						<Route
							exact
							path={RouteLinks.talentPool}
							element={
								<SuspenseLoader>
									<Page title="RefNow | Talent Pool">
										<TalentPool />
									</Page>
								</SuspenseLoader>
							}
						/>
					</Route>
					<Route
						exact
						path={RouteLinks.vacancyPool}
						element={
							<SuspenseLoader>
								<Page title="RefNow | Vacancy Pool">
									<VacancyPool />
								</Page>
							</SuspenseLoader>
						}
					/>
					<Route
						element={
							<ProtectedRoutes
								isAdmin={true}
								isSuperAdmin={true}
								navigateTo={`${RouteLinks.preview}?product=inbound`}
							/>
						}
					>
						<Route
							exact
							path={RouteLinks.inbound}
							element={
								<SuspenseLoader>
									<Page title="RefNow | Inbound Dashboard">
										<InboundDashboard />
									</Page>
								</SuspenseLoader>
							}
						/>
					</Route>
					<Route
						exact
						path={RouteLinks.settings.index}
						element={
							<SuspenseLoader>
								<Settings />
							</SuspenseLoader>
						}
					>
						<Route
							index
							element={
								<SuspenseLoader>
									<Page title="RefNow | Settings">
										<AccountSettings />
									</Page>
								</SuspenseLoader>
							}
						/>
						<Route
							exact
							path={RouteLinks.settings.notifications}
							element={
								<SuspenseLoader>
									<Page title="RefNow | Notification Settings">
										<NotificationSettings />
									</Page>
								</SuspenseLoader>
							}
						/>
						<Route
							exact
							path={RouteLinks.settings.helpCentre}
							element={
								<SuspenseLoader>
									<Page title="RefNow | Help Centre">
										<HelpCentre />
									</Page>
								</SuspenseLoader>
							}
						/>
						<Route element={<ProtectedRoutes isAdmin={true} isSuperAdmin={true} />}>
							<Route
								exact
								path={RouteLinks.settings.usersAndTeams}
								element={
									<SuspenseLoader>
										<Page title="RefNow | Users and Teams">
											<UsersAndTeams />
										</Page>
									</SuspenseLoader>
								}
							/>
						</Route>
						<Route element={<ProtectedRoutes isSuperAdmin={true} />}>
							<Route
								exact
								path={RouteLinks.settings.organisation}
								element={
									<SuspenseLoader>
										<Page title="RefNow | Org Settings">
											<OrganisationSettings />
										</Page>
									</SuspenseLoader>
								}
							/>
							<Route
								exact
								path={RouteLinks.settings.subscription}
								element={
									<SuspenseLoader>
										<Page title="RefNow | Subscription">
											<SubscriptionSettings />
										</Page>
									</SuspenseLoader>
								}
							/>
							<Route
								exact
								path={RouteLinks.settings.buyCredits}
								element={
									<SuspenseLoader>
										<Page title="RefNow | Buy Credits">
											<BuyCredits />
										</Page>
									</SuspenseLoader>
								}
							/>
						</Route>
					</Route>
				</Route>
			</Route>
			<Route
				path={RouteLinks.unauthorised}
				element={
					<Page title="RefNow | Unauthorised">
						<Unauthorised />
					</Page>
				}
			/>
		</Route>
	)
);

const container = document.getElementById('app');
const root = createRoot(container);

root.render(
	<ErrorBoundary>
		<PostHogProvider>
			<Provider store={store}>
				<MUIThemeProvider theme={customMuiTheme}>
					<IntlWrapper>
						<LocalizationProvider dateAdapter={AdapterMoment}>
							<GlobalStyles />
							<RouterProvider router={router} />
						</LocalizationProvider>
					</IntlWrapper>
				</MUIThemeProvider>
			</Provider>
		</PostHogProvider>
	</ErrorBoundary>
);

window.onload = (event) => {
	const { innerWidth, innerHeight } = event.currentTarget;

	store.dispatch(onWindowResize(innerWidth, innerHeight));
	store.dispatch(setDeviceType());
	store.dispatch(setOnlineStatus(window.navigator.onLine));
};
